import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ShortcutPage from './ShortcutPage';
import { LoginDialog, LogoutConfirmDialog } from './AuthDialogs';
import E3DMarketplace from './E3DMarketplace';
import CustomNFTMarketplace from './CustomNFTMarketplace';
import CustomNFTDialog from './CustomNFTDialog';
import NewsletterContent from './NewsletterContent';
import { OptionsPopup } from './OptionsPopup';
import { Button } from "@material-ui/core";
import { SERVER_NAME } from './config';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    backgroundColor: '#333',
    color: '#fff',
    boxSizing: 'border-box', // Ensure padding is included in the width
    '@media (max-width: 600px)': {
      padding: '8px 12px', // Reduced padding on mobile
      flexWrap: 'wrap', // Allow wrapping if needed
    },
  },
  mobileHeaderRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  walletButtonRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
  walletInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  // For controlling visibility on different screen sizes
  mobileOnly: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  desktopOnly: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  logo: {
    width: '32px',
    height: '32px',
    marginRight: '10px',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    '@media (max-width: 600px)': {
      fontSize: '1.25rem', // Slightly smaller on mobile
    },
  },
  loginButton: {
    backgroundColor: '#4d79ff',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '5px 15px',
    cursor: 'pointer',
    fontSize: '1rem',
    marginRight: '10px', // Add margin to the right
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
  },
  heroContainer: {
    minHeight: '100vh',
    backgroundColor: '#000000',
    padding: '20px', 
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'center',
    color: '#ffffff',
    '@media (orientation: landscape) and (max-height: 600px)': {
      padding: '10px',
      gap: '5px',
    },
  },
  heroTitle: {
    fontSize: '3rem',
    marginBottom: '10px',
    textShadow: '0 0 15px rgba(77, 121, 255, 0.8)',
  
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      marginBottom: '6px',
    },
  
    '@media (orientation: landscape) and (max-height: 500px)': {
      fontSize: '1.6rem',
      marginBottom: '4px',
    },
  },
  
  heroText: {
    fontSize: '1.25rem',
    maxWidth: '800px',
    marginBottom: '40px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: '20px',
    },
    '@media (orientation: landscape) and (max-height: 600px)': {
      fontSize: '1rem',
      marginBottom: '10px',
      maxWidth: '600px',
    },
  },  
  stackedSection: {
    position: 'relative',
    width: '100%',
    maxWidth: '1000px',
    minHeight: '420px',
    margin: '0 auto',
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    zIndex: 1,
  },
  backgroundGlobe: {
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    objectFit: 'cover',
    zIndex: 0,
    opacity: 0.65,
    pointerEvents: 'none',
    boxShadow: '0 0 60px 20px rgba(77, 121, 255, 0.35)',
  },  
  buttonLayer: {
    zIndex: 2,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',         // center each row
    alignItems: 'flex-start',
    gap: '20px',
    width: '100%',
    maxWidth: '880px',                // (4 × 200px) + gaps
    margin: '0 auto',                 // center the whole container
  },
  
  topButtonRow: {
    position: 'absolute',
    top: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    display: 'flex',
    gap: '15px',
    flexWrap: 'nowrap',
  },
  topButton: {
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    padding: '15px 25px',
    fontSize: '1rem',
    borderRadius: '20px',
    border: '2px solid #4d79ff',
    fontWeight: 'bold',
    position: 'relative',
    cursor: 'pointer',
  },
  newBadge: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    backgroundColor: '#ff6f00',
    color: '#fff',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  },
  gifImage: {
    width: "250px",
    height: "auto",
    objectFit: "cover", // Ensures the image scales nicely within the defined dimensions
    marginBottom: "5px",
  
    // Media query for portrait and small devices
    [theme.breakpoints.down('sm')]: {
      width: '100px',
      height: '100px', // Smaller dimensions for small devices

      '@media (orientation: landscape)': {
        width: '600px',
        height: '100px', // Adjust dimensions for landscape orientation
      },
    
      '@media (orientation: portrait)': {
        width: '300px',
        height: '300px', // Adjust dimensions for portrait orientation
      },
    },
  
  },  
  gifImageUsers: {
    width: "100%",
    height: "100%",
    objectFit: "cover", // Ensures the image scales nicely within the defined dimensions
    marginBottom: "5px",
    opacity: 0.5, // Set transparency to 100%
  
    // Media query for portrait and small devices
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%', // Smaller dimensions for small devices
  
      '@media (orientation: landscape)': {
        width: '100%',
        height: '100%', // Adjust dimensions for landscape orientation
      },
    
      '@media (orientation: portrait)': {
        width: '100%',
        height: '100%', // Adjust dimensions for portrait orientation
      },
    },
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "15px",
    "& button": {
      padding: "15px 30px",
      borderRadius: "25px",
      border: "none",
      fontSize: "1rem",
      cursor: "pointer",
      textTransform: "uppercase",
    },
  },
  btnPrimary: {
    backgroundColor: "#4d79ff",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#6c00ff",
    },
  },
  btnOutline: {
    backgroundColor: "transparent",
    border: "2px solid #ffffff",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#ffffff",
      color: "#4d79ff",
    },
  },
  aboutSection: {
    width: '100%',
    height: '100vh',
    backgroundImage: 'url(e3d_users.gif)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  aboutTitle: {
    fontSize: "2.5rem",
    marginBottom: "20px",
    color: "#4d79ff",
    textAlign: 'center',
  },
  aboutText: {
    fontSize: "1.2rem",
    maxWidth: "800px",
    margin: "0 auto 40px",
    textAlign: 'left',
  },
  featuresSection: {
    height: "100vh",
    padding: "50px 20px",
    backgroundImage: 'url(e3d_nft_view.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    textAlign: "center",
    color: "#333333", // Dark gray text for good contrast
    justifyContent: "center", // Center the columns horizontally
    alignItems: "center", // Center the columns vertically

    // Media query for portrait and small devices
    [theme.breakpoints.down('sm')]: {
      // Smaller dimensions for small devices
      '@media (orientation: portrait)': {
        height: "auto", // Adjust height to fit content
      },
    },
  },
  featuresContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    maxWidth: "800px",
    margin: "0 auto", // center the grid
    justifyContent: "center",
  
    [theme.breakpoints.down('sm')]: {
      '@media (orientation: portrait)': {
        gridTemplateColumns: "repeat(1, 1fr)",
      },
    },
  },
  featuresTitle: {
    marginTop: "70px",
    fontSize: "2.5rem",
    marginBottom: "10px",
    color: "#FFFFFF",
  },
  featuresText: {
    fontSize: "1.2rem",
    maxWidth: "800px",
    margin: "0 auto 40px",
    color: "#FFFFFF",
    textAlign: 'left',
  },
  featureCard: {
    width: "100%",
    maxWidth: "360px",
    backgroundColor: "#16213e",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
    textAlign: "left",
    color: "#ffffff",
    margin: "0 auto",
  },
  featureIcon: {
    display: "flex",
    alignItems: "center",
    paddingRight: "20px",
  },
  featureTitle: {
    fontSize: "1.5rem",
    marginBottom: "5px",
    paddingLeft: "20px",
    color: "#6c00ff",
  },
  newsletterSection: {
    backgroundColor: "#0f3460",
    padding: "10px",
    textAlign: "center",
    color: "#ffffff",
  },
  newsletterTitle: {
    fontSize: "2.5rem",
    marginBottom: "20px",
    color: "#4d79ff",
  },
  newsletterText: {
    fontSize: "1.2rem",
    maxWidth: "800px",
    margin: "0 auto 30px",
  },
  newsletterImage: {
    width: "400px",
    height: "auto",
    paddingBottom: "20px",
  },
  newsletterButton: {
    backgroundColor: "#4d79ff",
    padding: "15px 30px",
    borderRadius: "25px",
    color: "#ffffff",
    fontSize: "1rem",
    textTransform: "uppercase",
    border: "none",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#6c00ff",
    },
  },
  podcastSection: {
    background: 'linear-gradient(135deg, #e8f5e9, #dcedc8)', // Light green gradient
    padding: '10px',
    textAlign: 'center',
    color: '#333333',
  },
  podcastTitle: {
    fontSize: '2.5rem',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#6c63ff', // Accent color
  },
  podcastImage: {
    width: "400px",
    height: "auto",
    paddingBottom: "20px",
  },
  podcastDescription: {
    fontSize: '1.2rem',
    maxWidth: '800px',
    margin: '0 auto 30px',
    lineHeight: '1.6',
  },
  audioPlayerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px', // Adds space between the audio player and the button
    marginTop: '20px',
  },
  audioPlayer: {
    outline: 'none',
    width: '300px', // Fixed width for consistent alignment
    height: '40px', // Ensure consistent height with the button
    border: 'none',
    borderRadius: '10px',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
  },
  playButton: {
    padding: '15px 30px',
    fontSize: '1rem',
    borderRadius: '25px',
    border: 'none',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #6c63ff, #4d79ff)', // Gradient for button
    color: '#ffffff',
    transition: 'all 0.3s ease',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      background: 'linear-gradient(135deg, #4d79ff, #6c63ff)', // Reverse gradient on hover
      boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-2px)', // Slight lift on hover
    },
  },
  footer: {
    backgroundColor: "#0f3460",
    color: "#cccccc",
    textAlign: "center",
    padding: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerText: {
    fontSize: "0.9rem",
  },
  username: {
    marginRight: 10,
    fontSize: '0.85rem',
    color: '#ffffff',
    opacity: 0.9,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      opacity: 1,
      textDecoration: 'underline',
      fontWeight: 'bold'
    }
  },
  discordIcon: {
    fontSize: "1.5rem",
    color: "#ffffff",
    textDecoration: "none",
    "&:hover": {
      color: "#7289da",
    },
  },
  tokenSection: {
    backgroundColor: "#16213e",
    padding: "10px",
    textAlign: "center",
    color: "#ffffff",
  },
  tokenTitle: {
    fontSize: "2.5rem",
    marginBottom: "20px",
    color: "#4d79ff",
  },
  tokenText: {
    fontSize: "1.0rem",
    maxWidth: "800px",
    margin: "0 auto 30px",
    textAlign: 'left',
  },
  tokenLink: {
    fontSize: "1.2rem",
    color: "#4d79ff",
    textDecoration: "none",
    '&:hover': {
      textDecoration: "underline",
    },
  },
  tokenImage: {
    width: "400px",
    height: "auto",
    margin: "20px auto",
    display: "block",
  },
  section: {
    padding: "50px 20px",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: "#4d79ff",
    marginBottom: "20px",
  },
  sectionText: {
    fontSize: "1.2rem",
    maxWidth: "800px",
    margin: "0 auto 30px",
    color: "#FFFFFF",
    lineHeight: "1.6",
    textAlign: 'left', 
  },
  sectionTextCenter: {
    fontSize: "1.2rem",
    marginTop: "20px",
    maxWidth: "800px",
    margin: "0 auto 30px",
    color: "#FFFFFF",
    lineHeight: "1.6",
    textAlign: 'center', 
  },
  button: {
    backgroundColor: "#4d79ff",
    color: "#ffffff",
    padding: "15px 30px",
    fontSize: "1rem",
    borderRadius: "25px",
    border: "none",
    cursor: "pointer",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "#6c00ff",
    },
  },
  networkGraphSection: {
    height: "100vh",
    backgroundImage: 'url(e3d_pcap_short.gif)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: "50px 20px",
    textAlign: "center",
  },
  networkGraphSectionTitle: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: "#4d79ff",
    marginTop: "170px",
    marginBottom: "20px",
  },
  networkGraphContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    background: "#000000",
    borderRadius: "15px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
  },
  networkGraphImage: {
    width: "80%",
    maxWidth: "600px",
    height: "auto",
    borderRadius: "10px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
  },
  codeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(2),
  },
  codeBlock: {
    width: '700px',
    padding: theme.spacing(2),
    position: 'relative',
    backgroundColor: '#A9A9A9',
    border: '1px solid #ddd',
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    textAlign: 'left',
  },
  copyButton: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
  },
  nonMobile: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  username: {
    color: "#4d79ff",
    marginRight: "10px",
  },
  cmcNotice: {
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f9f9f9',
    border: '1px solid #ddd',
    borderRadius: '12px',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    color: '#333',
  },
  closeButton: {
    backgroundColor: 'transparent',
    color: '#ffffff',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    marginLeft: '10px',
    padding: '0 5px',
    '&:hover': {
      color: '#f0f0f0',
    },
  },
}));


function formatDeltaNarrative(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'pending — see OTA protocol §9';
  return `${n >= 0 ? '+' : ''}${n.toFixed(3)} AUC beyond price`;
}

const HeroSection = ({ setShowHomePage, setShowShortcutPage, setShowE3DMarketplace, setShowCustomNFTMarketplace, setShowSwapDialog, setShowStakeDialog, deltaE3D }) => {
  const classes = useStyles();

  return (
    <div className={classes.heroContainer}>
      <h1 className={classes.heroTitle}>List your token in one API call — no exchange form, no human reviewer, no wait.</h1>
      <p className={classes.heroText}>
        Every Ethereum token already has a page on e3d.ai. Claim yours by signing with the controlling wallet — optionally proving you're the contract's deployer or on-chain owner() — and paying a small E3D fee; owner-authored metadata attaches instantly, with no web form and no human in the loop, through a single API an agent can drive end to end. The same E3D Token unlocks the rest of the platform: E3D Maps for real-time blockchain traffic and routing conditions, and document tokenization that mints a file as a verifiable on-chain record of exact content, wallet, and time.
      </p>
      <p className={classes.heroText}>
        Underneath it all, E3D is a measurement system: transactions become stories, stories converge into theses, and theses are judged against explicit equations instead of black-box copy.
      </p>
      <div style={{ maxWidth: 900, marginBottom: 24, display: 'grid', gap: 10 }}>
        <div style={{ fontFamily: '"SFMono-Regular", Consolas, monospace', fontSize: 18, color: '#a7c5ff' }}>
          C^S_t = a_E E + a_R R + a_I I + a_F F + a_C C
        </div>
        <div style={{ fontFamily: '"SFMono-Regular", Consolas, monospace', fontSize: 18, color: '#a7c5ff' }}>
          Δ_E3D = AUC(price+e3d) − AUC(price_only)
        </div>
        <div style={{ fontSize: 14, color: '#d0dcff' }}>
          {`E3D adds ${formatDeltaNarrative(deltaE3D)} as of today.`}
        </div>
        <div style={{ fontSize: 13, color: '#d0dcff' }}>
          <a href="/methodology" style={{ color: '#9ec4ff', fontWeight: 800, textDecoration: 'none' }}>Read the paper and live methodology</a>
        </div>
      </div>

      <div className={classes.stackedSection}>
        <div className={classes.buttonLayer}>
          <ShortcutPage
            setShowHomePage={setShowHomePage}
            setShowShortcutPage={setShowShortcutPage}
            setShowE3DMarketplace={setShowE3DMarketplace}
            setShowCustomNFTMarketplace={setShowCustomNFTMarketplace}
            setShowSwapDialog={setShowSwapDialog}
            setShowStakeDialog={setShowStakeDialog}
          />
        </div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  const classes = useStyles();

  const features = [
    {
      title: "Token Registry & Claim API",
      description:
        "Agents and projects can claim an already-indexed token, attach owner-authored metadata, and get a scoped API key back — no browser form, no human reviewer in the loop.",
      icon: "🏷️",
    },
    {
      title: "E3D Maps: Blockchain Traffic Conditions",
      description:
        "Real-time route congestion, capital-flow signals, and navigation intelligence so agents can decide where and when to transact.",
      icon: "🚦",
    },
    {
      title: "Document Tokenization",
      description:
        "Mint any supported document as an NFT with e3d-tokenize, proving exact file content existed, unaltered, at a wallet address, at a point in time.",
      icon: "📄",
    },
    {
      title: "Real-Time 3D Visualization",
      description:
        "Explore Ethereum blockchain activities in real-time through a dynamic 3D sphere that displays token and NFT transactions.",
      icon: "🌐",
    },
    {
      title: "NFT Minting and Revenue Sharing",
      description:
        "Future: Mint NFTs from saved graphs with built-in revenue sharing for the original NFT owners, fostering a collaborative ecosystem.",
      icon: "🖼️",
    },
    {
      title: "Seamless Integration of Tokens and NFTs",
      description:
        "Interact with both token and NFT transactions seamlessly in one unified platform, reflecting real-world trends.",
      icon: "🔗",
    }, 
    {
      title: "Advanced Layouts and Customization",
      description:
        "Organic Galaxy is the primary transaction graph layout. Use supplementary layouts like Sphere, Grid, Pyramid, and Tube as alternate projections for specific perspectives.",
      icon: "📐",
    },
    {
      title: "Auto Refresh and Mobile Optimization",
      description:
        "Stay updated with auto-refreshing graphs and enjoy E3D’s modern functionality across mobile devices.",
      icon: "📱",
    },
    {
      title: "Blockchain Transaction Viewer",
      description:
        "Explore every blockchain transaction in detail, providing transparency and a comprehensive view of Ethereum activity.",
      icon: "🔍",
    },
    {
      title: "Token Volume Indicators",
      description:
        "Visualize transaction activity through token volume indicators, highlighting market trends and surges with ease.",
      icon: "📊",
    },
    {
      title: "Extensive Token Price and Market View",
      description:
        "Track real-time token prices, market caps, and historical trends to make informed, data-driven decisions.",
      icon: "💹",
    },
  ];

  return (
    <section className={classes.featuresSection}>
      <h2 className={classes.featuresTitle}>E3D Features</h2>
      <p className={classes.featuresText}>
        E3D is a real-time on-chain intelligence platform with an unparalleled suite of innovative features, transforming 
        complex data into an intuitive, interactive experience. By enabling users to explore tokens, NFTs, and 
        their relationships in real-time, E3D empowers a deeper understanding of blockchain activity through explainable 
        intelligence and investigation tools.
      </p>
      <div className={classes.featuresContainer}>
        {features.map((feature, index) => (
          <div key={index} className={classes.featureCard}>
            <div className={classes.featureIcon}>
              {feature.icon} <h2 className={classes.featureTitle}>{feature.title}</h2>
            </div>
            <p className={classes.featureDescription}>{feature.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};




const AboutSection = () => {
  const classes = useStyles();

  const features = [
    {
      title: "Unmatched Visualization",
      description:
        "Navigate the blockchain with ease using real-time, 3D graphs that bring your data to life.",
      icon: "🌐",
    },
    {
      title: "Token Swapping",
      description:
        "Perform secure transactions without leaving the visualization environment",
      icon: "🖼️",
    },
    {
      title: "Seamless Integration of Tokens and NFTs",
      description:
        "Turn your 3D blockchain snapshots into NFTs and monetize your creativity in our marketplace.",
      icon: "🔗",
    }, 
  ];

  return (
    <section className={classes.aboutSection}>
      <div>
        <h2 className={classes.aboutTitle}>Why Choose E3D?</h2>
        <p className={classes.aboutText}>
          E3D.ai is a real-time on-chain intelligence platform.
        </p>
        <p className={classes.aboutText}>
          By providing a unique and immersive experience, E3D.ai empowers users to 
          explore, analyze, and understand blockchain data in a new light. With real-time insights, advanced
          features, and a user-friendly interface, E3D.ai is the ultimate tool for blockchain enthusiasts, investors,
          and developers. Discover the future of blockchain exploration at E3D.ai.
        </p>
        <p className={classes.aboutText}>
          E3D is developed and operated by FutCo LLC.
        </p>
      </div>
      <div className={classes.featuresContainer}>
        {features.map((feature, index) => (
          <div key={index} className={classes.featureCard}>
            <div className={classes.featureIcon}>
              {feature.icon} <h2 className={classes.featureTitle}>{feature.title}</h2>
            </div>
            <p className={classes.featureDescription}>{feature.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}


const PodcastSection = () => {
  const classes = useStyles();

  const playPodcast = () => {
    // Access the global audio player
    const globalAudioPlayer = document.getElementById('global-audio-player');
  
    // Set the podcast source and play
    globalAudioPlayer.src = '/E3D_Podcast.wav'; // Replace with the actual path
    globalAudioPlayer.play().catch((error) => {
      console.error('Failed to play podcast:', error);
    });
  
    // Navigate to another page
    window.location.href = './token'; // Adjust path as needed
  };
  

  return (
    <section className={classes.podcastSection}>
      <h2 className={classes.podcastTitle}>E3D AI Podcast</h2>
      <p className={classes.podcastDescription}>
        Join our two expert commentators as they dive deep into the world of E3D, 
        explaining how this groundbreaking platform revolutionizes blockchain 
        intelligence. With engaging discussions, insightful analyses, and 
        stories of innovation, the E3D AI Podcast offers an exciting way to 
        discover the uniqueness of E3D. Learn how E3D turns blockchain data 
        into art and empowers users to explore and interact with the Ethereum 
        blockchain like never before.
      </p>      
      <div>
          <img src="e3d-podcast.png" alt="Podcast" className={classes.podcastImage} />
        </div>
      <div className={classes.audioPlayerContainer}>
        <audio
          className={classes.audioPlayer}
          controls
          src="/E3D_Podcast.wav" // Replace with the actual path to your podcast file
        >
          Your browser does not support the audio element.
        </audio>
        
      {/* 
        <button className={classes.playButton} onClick={playPodcast}>
          Play Podcast while using E3D
        </button>
      */}


      </div>
    </section>
  );
};


const TokenSection = () => {
  const classes = useStyles();
  return (
    <section className={classes.tokenSection}>
      <h2 className={classes.tokenTitle}>E3D Token</h2>
      <p className={classes.tokenText}>
        The E3D token is the native token of the E3D.ai platform, enabling transactions, premium features, and community rewards. 
        E3D tokens are ERC-20 compliant and are used to power the E3D.ai ecosystem, providing users with a seamless experience.
        Visit E3D.ai to learn more about how these tokens enhance your on-chain intelligence experience.
      </p>
      <p className={classes.tokenText}>
        E3D.ai tokens are available for purchase on the E3D.ai app, Uniswap, and Sushiswap. 
        There are separate liquidity pools for E3D tokens on Uniswap and Sushiswap.
      </p>
      <p className={classes.tokenText}>
        <strong>Contract Address:</strong> 0x6488861b401f427d13b6619c77c297366bcf6386
      </p>
      <img src="e3d_token.png" alt="E3D Token" className={classes.tokenImage} /> 
      <a 
        href="https://etherscan.io/token/0x6488861b401f427d13b6619c77c297366bcf6386" 
        target="_blank" 
        rel="noopener noreferrer" 
        className={classes.tokenLink}
      >
        View on Etherscan
      </a>
    </section>
  );
};


const CodeBlockWithCopyButton = () => {
  const classes = useStyles();

  const copyCode = () => {
    const codeBlock = document.getElementById('code-block').innerText;
    navigator.clipboard.writeText(codeBlock).then(() => {
      //alert('Code copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy code: ', err);
    });
  };

  return (
    <div className={classes.codeContainer}>
      <div className={classes.codeBlock} id="code-block">
        <pre>
          {`tshark -r test.pcap -T fields -e frame.number -e ip.src -e ip.dst -e frame.len -E separator=, -E occurrence=f -Y "ip" | gawk -F, '
BEGIN { OFS = "," }
{
  client_bytes[$2 OFS $3] += $4;
  server_bytes[$3 OFS $2] += $4;
}
END {
  print "Group,Client Addr,Server Addr,Client Bytes,Server Bytes,Events";
  for (i in client_bytes) { keys[++count] = i; }
  asort(keys);
  for (j = 1; j <= count; j++) {
    split(keys[j], s, OFS);
    client = client_bytes[keys[j]];
    server = server_bytes[keys[j]] ? server_bytes[keys[j]] : 0;
    print "Group1", s[1], s[2], client, server, 0;
  }
}' > test.csv`}
        </pre>
      </div>
      <Button className={classes.copyButton} onClick={copyCode}>
        Copy Command
      </Button>
    </div>
  );
};


const NetworkGraphSection = () => {
  const classes = useStyles();

  return (
    <section className={classes.networkGraphSection}>
      <h2 className={classes.networkGraphSectionTitle}>📡 Network Graph Visualization</h2>
      <p className={classes.sectionText}>
        E3D supports PCAP file visualization on non-mobile devices, allowing you to explore network traffic in a fully interactive 3D space.
        Upload your .pcap or .csv file, and watch the connections come to life in real-time.
      </p>
      <div className={classes.nonMobile}>
        <p className={classes.sectionText}>
            Behind the scenes, we use tshark and awk to process .pcap files and extract the IPv4 addresses and byte counts. 
            We do not store anything on the server, but if you would prefer to generate the .csv file locally, use this command:
        </p>

        <CodeBlockWithCopyButton />

        <p className={classes.sectionTextCenter}>
          Try it now and let us know what features you'd like to see next!
        </p>
        <a href="/pcap" style={{ textDecoration: "none" }}>
          <button className={classes.button}>Try It Now</button>
        </a>
      </div>
    </section>
  );
};


const NewsletterSection = ({subscribeNewsletter}) => {
  const classes = useStyles();
  
  return (
    <section className={classes.newsletterSection}>
      <h2 className={classes.newsletterTitle}>Stay Ahead with the E3D AI Newsletter</h2>
      <p className={classes.newsletterText}>
        Receive curated insights,
        market trends, and exclusive updates from the E3D platform. Stay
        informed about blockchain innovations and never miss a beat.
      </p>
      <div>
        <img src="e3d-newsletter.png" alt="Newsletter" className={classes.newsletterImage} />
      </div>
      <button
        className={classes.newsletterButton}
        onClick={() => subscribeNewsletter()}
      >
        Subscribe Now
      </button>
    </section>
  );
};


const HomePage = ({ setShowHomePage, setShowShortcutPage, setShowSignup, showLogin, setShowLogin, handleLogin, isAuthenticated, username, showLogoutConfirm, setShowLogoutConfirm, handleLogout, showE3DMarketplaceParam, showCustomNFTMarketplace, setShowCustomNFTMarketplace, setShowSwapDialog, setShowStakeDialog, showNewsletter, subscribeNewsletter: isSubscribedToNewsletter, setSubscribeNewsletter, tourSpeedGraph, setTourSpeedGraph, tourSpeedList, setTourSpeedList, spinSpeed, setSpinSpeed, imageSize, setImageSize, useAspectRatio, setUseAspectRatio, resetCookieConsent, setResetCookieConsent, showCustomNFTDialog, setShowCustomNFTDialog, userAddress }) => {
  const [showE3DMarketplace, setShowE3DMarketplace] = useState(showE3DMarketplaceParam);
  const [walletAddress, setWalletAddress] = useState(null);
  const [refreshKey, setRefreshKey] = useState(Date.now()); // Key to force newsletter refresh
  const [showOptionsPopup, setShowOptionsPopup] = useState(false);
  const [deltaE3D, setDeltaE3D] = useState(null);

  const classes = useStyles();

  useEffect(() => {
    setShowE3DMarketplace(showE3DMarketplaceParam);
  }, [showE3DMarketplaceParam]);
  
  // When newsletter becomes visible, update the refresh key to force content reload
  useEffect(() => {
    if (showNewsletter) {
      setRefreshKey(Date.now());
    }
  }, [showNewsletter]);

  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            setWalletAddress(accounts[0]);
          } else {
            setWalletAddress(null);
          }
        } catch (error) {
          console.error('Error fetching accounts', error);
        }
      }
    };
  
    checkConnection();
  
    const handleAccountsChanged = (accounts) => {
      if (accounts.length > 0) {
        setWalletAddress(accounts[0]);
      } else {
        setWalletAddress(null);
      }
    };
  
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
    }
  
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await fetch(`${SERVER_NAME}/methodology/backtest-summary`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (active) setDeltaE3D(json && json.current_delta_e3d != null ? Number(json.current_delta_e3d) : null);
      } catch (e) {
        if (active) setDeltaE3D(null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const subscribeNewsletter = () => {
    setShowSignup(true);
  };

  const connectWeb3 = async () => {
    if (window.ethereum) {
      if (!walletAddress) {
        try {
            const accounts = await window.ethereum.request({
                method: 'eth_requestAccounts'
            });
            setWalletAddress(accounts[0]);
        } catch (error) {
            console.error("Failed to get wallet access", error);
        }
      } else {
        disconnectWeb3();
      }
    } else {
        alert('MetaMask is not installed. Please consider installing it: https://metamask.io/download.html');
    }
  }; 

  const disconnectWeb3 = () => {
    setWalletAddress(null);
  };

  const optionsPopupClose = async (save, subscribeNewsletterParam) => {
    setShowOptionsPopup(false);
    if (save && subscribeNewsletterParam !== undefined) {
      setSubscribeNewsletter(subscribeNewsletterParam);
    }
  };

  const gotoMainPage = () => {
    // Reset the relevant states to ensure clean navigation
    setShowE3DMarketplace(false);
    setShowCustomNFTMarketplace(false);
    
    // If we're on the newsletter page, we need to ensure clean state
    if (window.location.pathname === '/news') {
      // Add a state flag to the URL to indicate we're navigating from newsletter
      window.location.href = '/?_t=' + Date.now();
    } else {
      // Normal navigation to homepage with a timestamp to prevent caching issues
      window.location.href = '/?_t=' + Date.now();
    }
  };
  
  return (
    <div className={classes.root}>
      {/* Options Popup */}
      {showOptionsPopup && (
        <OptionsPopup
          open={showOptionsPopup}
          onClose={optionsPopupClose}
          subscribeNewsletter={isSubscribedToNewsletter}
          setSubscribeNewsletter={setSubscribeNewsletter}
          tourSpeedGraph={tourSpeedGraph}
          setTourSpeedGraph={setTourSpeedGraph}
          tourSpeedList={tourSpeedList}
          setTourSpeedList={setTourSpeedList}
          spinSpeed={spinSpeed}
          setSpinSpeed={setSpinSpeed}
          imageSize={imageSize}
          setImageSize={setImageSize}
          useAspectRatio={useAspectRatio}
          setUseAspectRatio={setUseAspectRatio}
          resetCookieConsent={resetCookieConsent}
          setResetCookieConsent={setResetCookieConsent}
        />
      )}
      {showLogin && (
        <LoginDialog
          open={true}
          onLogin={handleLogin}
          onClose={() => setShowLogin(false)}
          setShowSignup={setShowSignup}
        />
      )}
      {showLogoutConfirm && (
        <LogoutConfirmDialog
            onLogout={handleLogout}
            onClose={() => setShowLogoutConfirm(false)}
          />
      )}

     {/* Main content area - displays marketplace, newsletter, or regular content */}
     {showE3DMarketplace ? (
      <div className={classes.marketplaceContainer}>
        <E3DMarketplace />
      </div>
     ) : showCustomNFTMarketplace ? (
      <div className={classes.marketplaceContainer}>
        <CustomNFTMarketplace />
      </div>
     ) : showCustomNFTDialog ? (
      <div className={classes.marketplaceContainer}>
        <CustomNFTDialog open={showCustomNFTDialog} onClose={() => setShowCustomNFTDialog(false)} userAddress={userAddress}/>
      </div>  
     ) : showNewsletter ? (
      <div className={classes.marketplaceContainer}>
        <NewsletterContent refreshKey={refreshKey} />
      </div>
     ) : (
        <>
          {/* Hero Section */}
          <HeroSection 
            setShowHomePage={setShowHomePage} 
            setShowShortcutPage={setShowShortcutPage}
            setShowE3DMarketplace={setShowE3DMarketplace}
            setShowCustomNFTMarketplace={setShowCustomNFTMarketplace}
            setShowSwapDialog={setShowSwapDialog}
            setShowStakeDialog={setShowStakeDialog}
            deltaE3D={deltaE3D}
          />

          {/* About Section */}
          <AboutSection />

          {/* Features Section */}
          <FeaturesSection />

          {/* Network Graph Section */}
          <NetworkGraphSection />

          {/* E3D Token Section */}
          <TokenSection />

          {/* Features Section */}
          <PodcastSection />

          {/* Footer */}
          <footer className={classes.footer}>
            <p className={classes.footerText}>E3D is a product developed and operated by FutCo LLC. © 2025 FutCo LLC. All rights reserved.</p>
            <a
              href="https://discord.gg/r2cvGF55"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.discordIcon}
            >
              <i className="fab fa-discord"></i>
            </a>
          </footer>
        </>
      )} 

    </div>
  );
};

export default HomePage;
